import React from "react";
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
  CircularProgress,
} from "@mui/material";
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";

interface ConfirmDeleteModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void | Promise<void>;
  loading?: boolean;
  itemName: string;
  itemType: string;
  title?: string;
  message?: string;
  infoMessage?: string;
  icon?: React.ReactNode;
  gradientColors?: [string, string];
  confirmButtonText?: string;
  cancelButtonText?: string;
}

const ConfirmDeleteModal: React.FC<ConfirmDeleteModalProps> = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  itemName,
  itemType,
  title = "Confirmar Exclusão",
  message = "Tem certeza que deseja excluir",
  infoMessage,
  icon = <DeleteIcon sx={{ fontSize: 48 }} />,
  gradientColors = ["#f44336", "#e91e63"],
  confirmButtonText = "Excluir",
  cancelButtonText = "Cancelar",
}) => {
  return (
    <Dialog
      open={open}
      onClose={() => !loading && onClose()}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: "hidden",
        },
      }}
    >
      {/* Header com gradiente */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${gradientColors[0]} 0%, ${gradientColors[1]} 100%)`,
          pt: 3,
          pb: 2,
          px: 3,
          position: "relative",
        }}
      >
        <IconButton
          onClick={onClose}
          disabled={loading}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: "white",
            "&:hover": { bgcolor: "rgba(255, 255, 255, 0.1)" },
          }}
        >
          <CloseIcon />
        </IconButton>

        <Box sx={{ textAlign: "center", color: "white" }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: "50%",
              bgcolor: "rgba(255, 255, 255, 0.2)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              margin: "0 auto",
              mb: 2,
              backdropFilter: "blur(10px)",
              border: "3px solid rgba(255, 255, 255, 0.3)",
            }}
          >
            {icon}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {title}
          </Typography>
        </Box>
      </Box>

      <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
        <Box sx={{ textAlign: "center" }}>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {message} <strong>{itemName}</strong>?
          </Typography>
          
          {infoMessage && (
            <Box
              sx={{
                bgcolor: "grey.50",
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                p: 2,
                mt: 2,
              }}
            >
              <Typography variant="body2" color="text.secondary">
                {infoMessage}
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          px: 3,
          pb: 3,
          pt: 0,
          gap: 1,
          flexDirection: { xs: "column", sm: "row" },
        }}
      >
        <Button
          fullWidth
          onClick={onClose}
          disabled={loading}
          variant="outlined"
          sx={{
            borderColor: "grey.300",
            color: "grey.700",
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            "&:hover": {
              borderColor: "grey.400",
              bgcolor: "grey.50",
            },
          }}
        >
          {cancelButtonText}
        </Button>
        <Button
          fullWidth
          onClick={onConfirm}
          variant="contained"
          disabled={loading}
          sx={{
            bgcolor: gradientColors[0],
            textTransform: "none",
            fontWeight: 600,
            py: 1.2,
            boxShadow: `0 4px 12px ${gradientColors[0]}4D`,
            "&:hover": {
              bgcolor: gradientColors[1],
              boxShadow: `0 6px 16px ${gradientColors[0]}66`,
            },
            "&:disabled": {
              bgcolor: "grey.300",
            },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <CircularProgress size={20} sx={{ color: "white" }} />
              Excluindo...
            </Box>
          ) : (
            confirmButtonText
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDeleteModal;